// Shared utilities for the Media Center feature.

import type { FolderTreeNode } from '@api/folders';

const IMAGE_MIME_PREFIXES = ['image/'];
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i;

export function isImageMime(mimeType: string, fileName: string): boolean {
  if (mimeType && IMAGE_MIME_PREFIXES.some((p) => mimeType.startsWith(p))) return true;
  return IMAGE_EXT.test(fileName);
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} ${units[i]}`;
}

/** Flatten the folder tree into an array of `{id, name, path, depth}` rows. */
export interface FlatFolder {
  id: string;
  name: string;
  path: string;
  depth: number;
}

export function flattenFolders(nodes: FolderTreeNode[], depth = 0): FlatFolder[] {
  const out: FlatFolder[] = [];
  for (const n of nodes) {
    out.push({ id: n.id, name: n.name, path: n.path, depth });
    if (n.children.length) out.push(...flattenFolders(n.children, depth + 1));
  }
  return out;
}

/** Find a folder node by id anywhere in the tree. */
export function findFolderInTree(
  nodes: FolderTreeNode[],
  id: string,
): FolderTreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const found = findFolderInTree(n.children, id);
    if (found) return found;
  }
  return null;
}
